import React, { useState } from 'react';
import {
	Box,
	Button,
	Container,
	Flex,
	Heading,
	Text,
	SimpleGrid,
	VStack,
	HStack,
	Icon,
	Badge,
	Card,
	useColorModeValue,
	Skeleton,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalCloseButton,
	useDisclosure,
} from '@chakra-ui/react';
import { FiCheckCircle } from 'react-icons/fi';
import {
	usePublicPricing,
	mapPublicPlanToTier,
	mapLifetimePlanToTier,
} from '../../../hooks/usePublicPricing';
import PublicPricingMatrix from '../../../components/PublicPricing/PublicPricingMatrix';
import { MONEY_BACK_GUARANTEE } from './homePageContent';
import Reveal from './Reveal';
import GlowBorderCard from './GlowBorderCard';

const BillingToggle = ({ isYearly, onChange, sampleAnnualSavings }) => (
	<HStack
		spacing={0}
		borderRadius='full'
		overflow='hidden'
		border='2px solid'
		borderColor='gray.200'
		maxW='260px'
		w='auto'
		flexShrink={0}
		position='relative'
		bg='gray.100'>
		<Box
			position='absolute'
			top='2px'
			bottom='2px'
			left={isYearly ? 'calc(50% - 2px)' : '2px'}
			w='calc(50% - 2px)'
			bg='white'
			borderRadius='full'
			transition='left 0.25s ease'
			zIndex={1}
			boxShadow='sm'
		/>
		<Box
			as='button'
			type='button'
			flex={1}
			py={2}
			px={4}
			color={isYearly ? 'gray.500' : 'gray.800'}
			fontWeight={isYearly ? '500' : '700'}
			fontSize='sm'
			cursor='pointer'
			position='relative'
			zIndex={2}
			transition='color 0.25s'
			onClick={() => onChange(false)}>
			Monthly
		</Box>
		<Box
			as='button'
			type='button'
			flex={1}
			py={2}
			px={4}
			color={!isYearly ? 'gray.500' : 'gray.800'}
			fontWeight={!isYearly ? '500' : '700'}
			fontSize='sm'
			cursor='pointer'
			position='relative'
			zIndex={2}
			transition='color 0.25s'
			onClick={() => onChange(true)}>
			<HStack justify='center' spacing={1}>
				<Text m={0}>Annual</Text>
				{sampleAnnualSavings ? (
					<Badge
						colorScheme='green'
						fontSize='9px'
						borderRadius='full'
						px={1.5}
						display={{ base: 'none', sm: 'inline-flex' }}>
						Save ~{sampleAnnualSavings}%
					</Badge>
				) : null}
			</HStack>
		</Box>
	</HStack>
);

const PlanTypeTabs = ({ pricingTab, onChange }) => (
	<Flex
		justify='center'
		gap={2}
		p={1.5}
		bg='gray.100'
		borderRadius='full'
		border='1px solid'
		borderColor='gray.200'
		w='fit-content'
		mx='auto'
		mb={6}>
		<Button
			variant={pricingTab === 'saas' ? 'solid' : 'ghost'}
			colorScheme='teal'
			bg={pricingTab === 'saas' ? 'white' : 'transparent'}
			color={pricingTab === 'saas' ? 'gray.800' : 'gray.600'}
			boxShadow={pricingTab === 'saas' ? 'sm' : 'none'}
			borderRadius='full'
			px={6}
			size='md'
			_hover={{ bg: pricingTab === 'saas' ? 'white' : 'gray.200' }}
			onClick={() => onChange('saas')}>
			Subscription
		</Button>
		<Button
			variant={pricingTab === 'lifetime' ? 'solid' : 'ghost'}
			colorScheme='orange'
			bg={pricingTab === 'lifetime' ? 'white' : 'transparent'}
			color={pricingTab === 'lifetime' ? 'gray.800' : 'gray.600'}
			boxShadow={pricingTab === 'lifetime' ? 'sm' : 'none'}
			borderRadius='full'
			px={6}
			size='md'
			_hover={{ bg: pricingTab === 'lifetime' ? 'white' : 'gray.200' }}
			onClick={() => onChange('lifetime')}>
			Lifetime access
		</Button>
	</Flex>
);

/** Responsive columns: up to maxPerRow on xl, shrinks when fewer tiers exist. */
const getPricingGridColumns = (tierCount, tab = 'saas') => {
	const count = Math.max(tierCount, 1);
	const maxPerRow = tab === 'lifetime' ? 3 : 4;

	return {
		base: 1,
		md: Math.min(count, 2),
		xl: Math.min(count, maxPerRow),
	};
};

const PricingTierBody = ({
	tier,
	isAnnual,
	textColor,
	goSignup,
	primaryCtaSx,
	onViewAllFeatures,
}) => {
	const isLifetime = tier.pricingMode === 'lifetime';

	return (
		<VStack align='stretch' spacing={0} h='full' flex={1}>
			{tier.highlight && (
				<Badge
					position='absolute'
					top={3}
					left='50%'
					transform='translateX(-50%)'
					colorScheme='cyan'
					rounded='full'
					px={3}>
					Most popular
				</Badge>
			)}
			{isLifetime && (
				<Badge
					position='absolute'
					top={3}
					right={3}
					colorScheme='orange'
					rounded='full'
					px={2}
					fontSize='10px'>
					Lifetime
				</Badge>
			)}
			<Text
				fontWeight='bold'
				fontSize='sm'
				textTransform='uppercase'
				letterSpacing='wider'
				color={tier.highlight ? 'cyan.300' : 'gray.500'}
				mb={2}
				mt={tier.highlight ? 6 : 0}
				pr={isLifetime ? 16 : 0}>
				{tier.name}
			</Text>
			<Heading size='2xl' mb={1} color={tier.highlight ? 'white' : 'gray.900'}>
				{isLifetime ? (
					tier.lifetimePrice != null ? (
						<>
							${tier.lifetimePrice}
							<Text as='span' fontSize='md' fontWeight='normal' opacity={0.7}>
								{' '}
								once
							</Text>
						</>
					) : (
						'Lifetime'
					)
				) : tier.monthly == null ? (
					'Custom'
				) : (
					<>
						{isAnnual
							? tier.yearlyFormatted ?? `$${tier.yearly}`
							: `$${tier.monthly}`}
						<Text as='span' fontSize='md' fontWeight='normal' opacity={0.7}>
							{isAnnual ? '/yr' : '/mo'}
						</Text>
					</>
				)}
			</Heading>
			<Box mb={5} />
			<Button
				w='full'
				mb={6}
				size='md'
				rounded='xl'
				bg={tier.highlight ? 'cyan.400' : undefined}
				color={tier.highlight ? 'gray.900' : undefined}
				colorScheme={tier.highlight ? undefined : isLifetime ? 'orange' : 'blue'}
				variant={
					!isLifetime && tier.monthly == null ? 'outline' : isLifetime ? 'solid' : 'outline'
				}
				onClick={
					isLifetime || tier.monthly != null ? goSignup : undefined
				}
				_hover={tier.highlight ? { bg: 'cyan.300' } : undefined}
				sx={tier.highlight ? primaryCtaSx : undefined}>
				{isLifetime
					? 'Get lifetime access'
					: tier.monthly == null
						? 'Contact sales'
						: 'Choose plan'}
			</Button>
			<VStack align='start' spacing={3} flex={1}>
				{tier.features.map((f) => (
					<HStack key={f} align='start'>
						<Icon
							as={FiCheckCircle}
							color={tier.highlight ? 'cyan.400' : isLifetime ? 'orange.400' : 'blue.500'}
							mt={0.5}
						/>
						<Text
							fontSize='xs'
							color={tier.highlight ? 'whiteAlpha.900' : 'gray.600'}
							mb={0}
							lineHeight='1.4'>
							{f}
						</Text>
					</HStack>
				))}
			</VStack>
			{onViewAllFeatures && (
				<Button
					w='full'
					mt='auto'
					pt={5}
					size='sm'
					variant='ghost'
					color={tier.highlight ? 'cyan.200' : isLifetime ? 'orange.600' : 'blue.600'}
					_hover={{
						bg: tier.highlight ? 'whiteAlpha.100' : isLifetime ? 'orange.50' : 'blue.50',
					}}
					onClick={onViewAllFeatures}>
					View all features
				</Button>
			)}
		</VStack>
	);
};

const PricingTierGrid = ({
	tiers,
	isAnnual,
	textColor,
	cardBg,
	goSignup,
	primaryCtaSx,
	onViewAllFeatures,
	columns = { base: 1, md: 2, xl: 4 },
}) => (
	<SimpleGrid columns={columns} spacing={6} alignItems='stretch'>
		{tiers.map((tier, i) => (
			<Reveal key={tier.name} delay={i * 0.08}>
				{tier.highlight ? (
					<GlowBorderCard h='full'>
						<Card
							bg='gray.900'
							color='white'
							rounded='2xl'
							p={6}
							h='full'
							display='flex'
							flexDirection='column'
							border='none'
							boxShadow='2xl'
							position='relative'
							transform={{ xl: 'scale(1.02)' }}>
							<PricingTierBody
								tier={tier}
								isAnnual={isAnnual}
								textColor={textColor}
								goSignup={goSignup}
								primaryCtaSx={primaryCtaSx}
								onViewAllFeatures={onViewAllFeatures}
							/>
						</Card>
					</GlowBorderCard>
				) : (
					<Card
						bg={cardBg}
						rounded='2xl'
						p={6}
						h='full'
						display='flex'
						flexDirection='column'
						border='2px solid'
						borderColor='gray.100'
						boxShadow='sm'
						position='relative'
						_hover={{ transform: 'translateY(-4px)', boxShadow: 'md' }}
						transition='all 0.25s'>
						<PricingTierBody
							tier={tier}
							isAnnual={isAnnual}
							textColor={textColor}
							goSignup={goSignup}
							onViewAllFeatures={onViewAllFeatures}
						/>
					</Card>
				)}
			</Reveal>
		))}
	</SimpleGrid>
);

const HomePagePricingSection = ({ goSignup, primaryCtaSx }) => {
	const [selectedPricingTab, setPricingTab] = useState('saas');
	const [isAnnual, setIsAnnual] = useState(false);
	const [matrixCatalog, setMatrixCatalog] = useState('saas');
	const {
		isOpen: featuresModalOpen,
		onOpen: openFeaturesModal,
		onClose: closeFeaturesModal,
	} = useDisclosure();

	const { data: pricingCatalog, isLoading: pricingLoading } = usePublicPricing();

	const saasTiers = (pricingCatalog?.plans || []).map(mapPublicPlanToTier);
	const lifetimeTiers = (pricingCatalog?.lifetimePlans || []).map(
		mapLifetimePlanToTier,
	);

	const hasSaasTiers = saasTiers.length > 0;
	const hasLifetimeTiers = lifetimeTiers.length > 0;
	const showPlanTypeTabs = pricingLoading || hasSaasTiers === hasLifetimeTiers;

	let pricingTab = selectedPricingTab;
	if (!pricingLoading) {
		if (pricingTab === 'saas' && !hasSaasTiers && hasLifetimeTiers) {
			pricingTab = 'lifetime';
		} else if (pricingTab === 'lifetime' && !hasLifetimeTiers && hasSaasTiers) {
			pricingTab = 'saas';
		}
	}

	const activeTiers = pricingTab === 'lifetime' ? lifetimeTiers : saasTiers;

	const sampleAnnualSavings = pricingCatalog?.plans?.find(
		(plan) => plan.pricing?.annualSavings?.percentOff,
	)?.pricing?.annualSavings?.percentOff;

	const textColor = useColorModeValue('gray.600', 'gray.300');
	const cardBg = useColorModeValue('white', 'gray.800');

	const handleOpenFeatures = () => {
		setMatrixCatalog(pricingTab);
		openFeaturesModal();
	};

	const matrixPlans =
		matrixCatalog === 'lifetime'
			? pricingCatalog?.lifetimePlans || []
			: pricingCatalog?.plans || [];
	const matrixFeatureRows =
		matrixCatalog === 'lifetime'
			? pricingCatalog?.lifetimeFeatureRows || []
			: pricingCatalog?.featureRows || [];

	const gridColumns = getPricingGridColumns(
		pricingLoading
			? pricingTab === 'lifetime'
				? lifetimeTiers.length || 3
				: saasTiers.length || 4
			: activeTiers.length,
		pricingTab,
	);

	const skeletonCount =
		pricingTab === 'lifetime'
			? Math.max(lifetimeTiers.length, 3)
			: Math.max(saasTiers.length, 4);

	return (
		<Box py={{ base: 16, md: 24 }} bg='gray.50' id='pricing'>
			<Container maxW='container.xl'>
				<Reveal>
					<VStack spacing={4} textAlign='center' mb={8}>
						<Heading size='2xl' color='gray.900' fontWeight='extrabold'>
							Plans that grow with your demands.
						</Heading>
						<Text color={textColor} fontSize='lg' maxW='2xl'>
							Choose a flexible subscription or buy lifetime access with a single
							one-time payment.
						</Text>
					</VStack>
				</Reveal>

				{showPlanTypeTabs && (
					<Reveal delay={0.05}>
						<PlanTypeTabs pricingTab={pricingTab} onChange={setPricingTab} />
					</Reveal>
				)}

				{pricingTab === 'saas' && (
					<Reveal delay={0.06}>
						<Flex justify='center' mb={6}>
							<HStack
								spacing={2}
								bg='green.50'
								border='1px solid'
								borderColor='green.200'
								px={4}
								py={2}
								rounded='full'>
								<Icon as={FiCheckCircle} color='green.600' />
								<Text fontSize='xs' fontWeight='semibold' color='green.800' mb={0}>
									{MONEY_BACK_GUARANTEE.title} on every paid plan
								</Text>
							</HStack>
						</Flex>
					</Reveal>
				)}

				{pricingTab === 'lifetime' && (
					<Reveal delay={0.06}>
						<Box
							mb={6}
							mx='auto'
							maxW='2xl'
							textAlign='center'
							bg='orange.50'
							border='1px solid'
							borderColor='orange.200'
							rounded='xl'
							px={5}
							py={4}>
							<Text fontSize='xs' color='orange.900' mb={0} lineHeight='tall'>
								<strong>Lifetime access</strong> — pay once in-app, no recurring
								subscription. Pick the tier that fits your team and upgrade anytime
								by moving to a higher lifetime plan.
							</Text>
						</Box>
					</Reveal>
				)}

				<Box
					bg='white'
					borderRadius='2xl'
					border='1px solid'
					borderColor='gray.100'
					boxShadow='sm'
					overflow='hidden'
					mb={2}>
					<Box
						px={{ base: 4, md: 6 }}
						py={4}
						borderBottom='1px solid'
						borderColor='gray.100'
						bg='gray.50'>
						<Flex
							justify='space-between'
							align='center'
							flexWrap='wrap'
							gap={3}>
							<Text fontWeight='700' color='gray.800' m={0} fontSize='sm'>
								{pricingTab === 'lifetime'
									? 'Compare lifetime plans'
									: 'Compare subscription plans'}
							</Text>
							{pricingTab === 'saas' ? (
								<BillingToggle
									isYearly={isAnnual}
									onChange={setIsAnnual}
									sampleAnnualSavings={sampleAnnualSavings}
								/>
							) : (
								<Badge colorScheme='orange' borderRadius='full' px={3}>
									One-time payment
								</Badge>
							)}
						</Flex>
					</Box>

					<Box px={{ base: 4, md: 6 }} py={6}>
				{pricingLoading ? (
					<SimpleGrid columns={gridColumns} spacing={6}>
						{Array.from({ length: skeletonCount }).map((_, index) => (
							<Skeleton key={index} height='420px' borderRadius='2xl' />
						))}
					</SimpleGrid>
				) : activeTiers.length > 0 ? (
					<PricingTierGrid
						tiers={activeTiers}
						isAnnual={isAnnual}
						textColor={textColor}
						cardBg={cardBg}
						goSignup={goSignup}
						primaryCtaSx={primaryCtaSx}
						onViewAllFeatures={handleOpenFeatures}
						columns={gridColumns}
					/>
				) : (
					<Text textAlign='center' color={textColor} fontSize='sm'>
						{pricingTab === 'lifetime'
							? 'Lifetime plans are coming soon. Check back or contact sales.'
							: 'Pricing is temporarily unavailable. Please check back soon or contact sales.'}
					</Text>
				)}
					</Box>
				</Box>
			</Container>

			<Modal
				isOpen={featuresModalOpen}
				onClose={closeFeaturesModal}
				size='6xl'
				scrollBehavior='outside'
				isCentered>
				<ModalOverlay bg='blackAlpha.600' backdropFilter='blur(4px)' />
				<ModalContent mx={4} maxH='90vh'>
					<ModalHeader borderBottom='1px solid' borderColor='gray.100' py={4}>
						<Flex
							justify='space-between'
							align='center'
							flexWrap='wrap'
							gap={3}
							w='full'
							pr={8}>
							<Text fontWeight='700' fontSize='md' m={0}>
								{matrixCatalog === 'lifetime'
									? 'Compare lifetime plans'
									: 'Compare subscription plans'}
							</Text>
							{matrixCatalog === 'saas' ? (
								<BillingToggle
									isYearly={isAnnual}
									onChange={setIsAnnual}
									sampleAnnualSavings={sampleAnnualSavings}
								/>
							) : (
								<Badge colorScheme='orange' borderRadius='full' px={3}>
									One-time payment
								</Badge>
							)}
						</Flex>
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody py={6}>
						<PublicPricingMatrix
							plans={matrixPlans}
							featureRows={matrixFeatureRows}
							inModal
							isYearly={matrixCatalog === 'saas' ? isAnnual : false}
						/>
					</ModalBody>
				</ModalContent>
			</Modal>
		</Box>
	);
};

export default HomePagePricingSection;
